import { Button } from "@/components/ui/button";
import { Form, FormField, FormItem, FormLabel } from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { scheduleSchema, ScheduleSchema } from "@/lib/api/user";
import { formatTimeTo12Hour } from "@/lib/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2, Save } from "lucide-react";
import { useEffect, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { ScheduleSimulator } from "./schedule-simulator";
import { usaTimezones } from "@/lib/utils/timezone";

const daysOfWeek = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

const generateTimeOptions = () => {
  const options = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      const time24 = `${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`;
      const time12 = formatTimeTo12Hour(time24);
      options.push({ value: time24, label: time12 });
    }
  }
  return options;
};

const timeOptions = generateTimeOptions();

interface ScheduleSettingsUIProps {
  defaultValues: ScheduleSchema;
  onSubmit: (data: ScheduleSchema) => Promise<void>;
  isPending: boolean;
  userName?: string;
  disabled?: boolean;
}

export function ScheduleSettingsUI({
  defaultValues,
  onSubmit,
  isPending,
  userName,
  disabled = false,
}: ScheduleSettingsUIProps) {
  const form = useForm<ScheduleSchema>({
    resolver: zodResolver(scheduleSchema),
    defaultValues,
  });

  const handleSubmit = async (data: ScheduleSchema) => {
    await onSubmit(data);
  };

  useEffect(() => {
    form.reset(defaultValues);
  }, [defaultValues, form]);

  const watchedSchedule = form.watch("schedule");
  const watchedTimezone = form.watch("timezone");

  const scheduleData = useMemo(
    () => ({
      schedule: watchedSchedule,
      timezone: watchedTimezone,
    }),
    [watchedSchedule, watchedTimezone],
  );

  return (
    <div className="rounded-xl border border-gray-100 p-4">
      <h2 className="text-lg font-bold">Active Hours</h2>
      <p className="mb-4 mt-1 text-sm text-gray-600">
        Set {userName ? `${userName}'s` : "your"} active hours for each day.
        These hours will be considered as
        {userName ? " their" : " your"} available time for mentoring. Disabling
        a day means {userName ? "they're" : "you're"}
        offline on that day.
      </p>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(handleSubmit)}
          className="mt-6 space-y-6"
        >
          <div className="space-y-4">
            <FormField
              control={form.control}
              name="timezone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Timezone</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                    disabled={disabled}
                  >
                    <SelectTrigger className="w-[300px]">
                      <SelectValue placeholder="Select your timezone" />
                    </SelectTrigger>
                    <SelectContent>
                      {usaTimezones.map((tz) => (
                        <SelectItem key={tz} value={tz}>
                          {tz}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="autoDetectTimezone"
              render={({ field }) => (
                <FormItem className="flex items-center space-x-2">
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    id="auto-detect-timezone"
                    disabled={disabled}
                  />
                  <FormLabel
                    htmlFor="auto-detect-timezone"
                    className="cursor-pointer"
                  >
                    Automatically detect timezone
                  </FormLabel>
                </FormItem>
              )}
            />
          </div>

          {daysOfWeek.map((day, index) => (
            <div key={day} className="flex items-center space-x-4">
              <span className="w-24">{day}</span>
              <Controller
                name={`schedule.${index}.isOnline`}
                control={form.control}
                render={({ field }) => (
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    disabled={disabled}
                  />
                )}
              />
              <FormField
                control={form.control}
                name={`schedule.${index}.startTime`}
                render={({ field }) => (
                  <FormItem>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                      disabled={disabled}
                    >
                      <SelectTrigger className="w-[140px]">
                        <SelectValue placeholder="Start time">
                          {field.value
                            ? formatTimeTo12Hour(field.value)
                            : "Start time"}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {timeOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name={`schedule.${index}.endTime`}
                render={({ field }) => (
                  <FormItem>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                      disabled={disabled}
                    >
                      <SelectTrigger className="w-[140px]">
                        <SelectValue placeholder="End time">
                          {field.value
                            ? formatTimeTo12Hour(field.value)
                            : "End time"}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {timeOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormItem>
                )}
              />
            </div>
          ))}
          <div className="flex justify-end">
            <Button type="submit" disabled={isPending || disabled}>
              {isPending ? (
                <Loader2 className="mr-1 h-5 w-5 animate-spin" />
              ) : (
                <Save className="mr-1 h-5 w-5" />
              )}
              Save
            </Button>
          </div>
        </form>
      </Form>
      <ScheduleSimulator scheduleData={scheduleData} />
    </div>
  );
}
